/**
 * Un cont, mai multe roluri — și comutatorul între ele.
 *
 * The data model always allowed it: `user_roles` is many-to-many with a scope,
 * so one email can be a coach of two teams and an athlete in a third. What was
 * missing was a way to USE the second role. The app picks one `activeRole` and
 * keys every screen off it, and the only switch on offer was a hard-coded
 * "Mod Admin Club" for coaches who also administered the club — so a coach who
 * also trains in a squad had no way to see their own attendance, points or
 * leaderboard without a second account on a second email address.
 *
 * This lists the modes a person actually holds, in a fixed order, with where
 * each one lives. Nothing here grants anything: a mode appears only because
 * the server already said they hold that role.
 */

export type RoleModeCode =
  | "platform_admin"
  | "club_admin"
  | "coordinator"
  | "coach"
  | "athlete"
  | "parent";

export type RoleMode = {
  code: RoleModeCode;
  /** As it reads in the menu: "Mod Sportiv". */
  label: string;
  /** Where that mode starts. Must match routes.tsx's DashboardRedirect. */
  home: string;
};

/**
 * Desk-work roles first, then the app roles. The order is the same one the
 * server uses to pick a default (`pickActiveRole`), so the list never implies
 * a precedence the rest of the system disagrees with.
 */
const MODES: RoleMode[] = [
  { code: "platform_admin", label: "Mod Admin Platformă", home: "/dashboard/admin-platforma" },
  { code: "club_admin", label: "Mod Admin Club", home: "/dashboard/admin-club" },
  { code: "coordinator", label: "Mod Coordonator", home: "/dashboard/admin-club" },
  { code: "coach", label: "Mod Antrenor", home: "/app/coach" },
  { code: "athlete", label: "Mod Sportiv", home: "/app" },
  { code: "parent", label: "Mod Părinte", home: "/app/dashboard" },
];

/** Every mode this person holds, whatever the scope of the role. */
export function modesFor(roles: { code: string }[] | null | undefined): RoleMode[] {
  const held = new Set((roles || []).map((r) => r.code));
  return MODES.filter((m) => held.has(m.code));
}

/**
 * The modes worth offering as a switch: the ones they hold, minus the one they
 * are already in.
 *
 * Returns an empty list for the great majority of accounts, which hold exactly
 * one role — and the callers render nothing at all for an empty list, so the
 * menu does not grow a section that says "you are what you are".
 */
export function otherModesFor(
  roles: { code: string }[] | null | undefined,
  activeRole: string | null | undefined
): RoleMode[] {
  return modesFor(roles).filter((m) => m.code !== activeRole);
}

export function modeLabel(code: string): string {
  return MODES.find((m) => m.code === code)?.label || code;
}
